
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigResponse} from './ConfigResponse';
import {FieldModelConfigResponse} from './ConfigResponse';
import {ConfigValue} from './ConfigValue';
import {FieldModelConfigValue} from './ConfigValue';

/**
 * ConfigSnapshotResponse struct
 */
class ConfigSnapshotResponse extends ConfigResponse {
  /**
   * Initialize struct
   * @param {!ConfigResponse=} parent
   * @param {!string=} keys
   * @param {!Map=} snapshot
   * @constructor
   */
  constructor (parent = new ConfigResponse(), keys = '', snapshot = new Map()) {
    super()
    super.copy(parent)
    this.Keys = keys
    this.Snapshot = snapshot
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ConfigSnapshotResponse}
   * @param {!ConfigSnapshotResponse} other Other struct
   * @returns {!ConfigSnapshotResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.Keys != null) {
      this.Keys = other.Keys
    } else {
      this.Keys = null
    }
    if (other.Snapshot != null) {
      this.Snapshot = new Map()
      Object.keys(other.Snapshot).forEach(key => {
        if (key != null) {
          let tempKey
          tempKey = key
          if (other.Snapshot[key] != null) {
            let tempValue
            tempValue = ConfigValue.fromObject(other.Snapshot[key])
            this.Snapshot.set(tempKey, tempValue)
          } else {
            this.Snapshot.set(tempKey, null)
          }
        }
      })
    } else {
      this.Snapshot = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ConfigSnapshotResponse}
   * @returns {!ConfigSnapshotResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ConfigSnapshotResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ConfigSnapshotResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ConfigSnapshotResponse}
   * @param {!ConfigSnapshotResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ConfigSnapshotResponse)) {
      throw new TypeError('Instance of ConfigSnapshotResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ConfigSnapshotResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      Keys: ((this.Keys != null) ? this.Keys : null),
      Snapshot: ((this.Snapshot != null) ? fbe.MapToObject(new Map(Array.from(this.Snapshot, item => [((item[0] != null) ? item[0] : null), ((item[1] != null) ? item[1] : null)]))) : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ConfigSnapshotResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ConfigSnapshotResponse} other Object value
   * @returns {!ConfigSnapshotResponse} Created struct
   */
  static fromObject (other) {
    return new ConfigSnapshotResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ConfigSnapshotResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ConfigSnapshotResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ConfigSnapshotResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 1008
  }
}

export { ConfigSnapshotResponse };

/**
 * Fast Binary Encoding ConfigSnapshotResponse field model
 */
class FieldModelConfigSnapshotResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigResponse(buffer, 4 + 4)
    this.Keys = new fbe.FieldModelString(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.Snapshot = new fbe.FieldModelMap(new fbe.FieldModelString(buffer, this.Keys.fbeOffset + this.Keys.fbeSize), new FieldModelConfigValue(buffer, this.Keys.fbeOffset + this.Keys.fbeSize), buffer, this.Keys.fbeOffset + this.Keys.fbeSize)
  }

  /**
   * Get the ConfigResponse field model
   * @this {!FieldModelConfigSnapshotResponse}
   * @returns {!FieldModelConfigResponse} ConfigResponse field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelConfigSnapshotResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelConfigSnapshotResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.Keys.fbeSize + this.Snapshot.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelConfigSnapshotResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.Keys.fbeExtra + this.Snapshot.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelConfigSnapshotResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelConfigSnapshotResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelConfigSnapshotResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 1008
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelConfigSnapshotResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelConfigSnapshotResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Keys.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Keys.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Keys.fbeSize

    if ((fbeCurrentSize + this.Snapshot.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Snapshot.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Snapshot.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelConfigSnapshotResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelConfigSnapshotResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelConfigSnapshotResponse}
   * @param {!ConfigSnapshotResponse} fbeValue Default value, defaults is new ConfigSnapshotResponse()
   * @returns {!ConfigSnapshotResponse} ConfigSnapshotResponse value
   */
  get (fbeValue = new ConfigSnapshotResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelConfigSnapshotResponse}
   * @param {!ConfigSnapshotResponse} fbeValue ConfigSnapshotResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Keys.fbeSize) <= fbeStructSize) {
      fbeValue.Keys = this.Keys.get()
    } else {
      fbeValue.Keys = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Keys.fbeSize

    if ((fbeCurrentSize + this.Snapshot.fbeSize) <= fbeStructSize) {
      this.Snapshot.get(fbeValue.Snapshot)
    } else {
      fbeValue.Snapshot.clear()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Snapshot.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelConfigSnapshotResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelConfigSnapshotResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelConfigSnapshotResponse}
   * @param {!ConfigSnapshotResponse} fbeValue ConfigSnapshotResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelConfigSnapshotResponse}
   * @param {ConfigSnapshotResponse} fbeValue ConfigSnapshotResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.Keys.set(fbeValue.Keys)
    this.Snapshot.set(fbeValue.Snapshot)
  }
}

export { FieldModelConfigSnapshotResponse };

/**
 * Fast Binary Encoding ConfigSnapshotResponse model
 */
class ConfigSnapshotResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelConfigSnapshotResponse(this.buffer, 4)
  }

  /**
   * Get the ConfigSnapshotResponse model
   * @this {!ConfigSnapshotResponseModel}
   * @returns {!FieldModelConfigSnapshotResponse} model ConfigSnapshotResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ConfigSnapshotResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ConfigSnapshotResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ConfigSnapshotResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ConfigSnapshotResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelConfigSnapshotResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ConfigSnapshotResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ConfigSnapshotResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ConfigSnapshotResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ConfigSnapshotResponseModel}
   * @param {!ConfigSnapshotResponse} value ConfigSnapshotResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ConfigSnapshotResponseModel}
   * @param {!ConfigSnapshotResponse} value ConfigSnapshotResponse value, defaults is new ConfigSnapshotResponse()
   * @return {!object} Deserialized ConfigSnapshotResponse value and its size
   */
  deserialize (value = new ConfigSnapshotResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ConfigSnapshotResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ConfigSnapshotResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ConfigSnapshotResponseModel}
   * @param {!number} prev Previous ConfigSnapshotResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ConfigSnapshotResponseModel };
